/**
 * Store-specific behavior. Everything here is vanilla JS (no jQuery) and progressively
 * enhances markup that already works without JavaScript.
 */

document.addEventListener('DOMContentLoaded', function () {
  initProductFaq();
  initFragranceFinder();
  initScrollReveal();
  initHeroSlideshow();
  initBrandIndex();
  initLazyImageFadeInFix();
  initCollectionFacets();
  initProductDescriptionReadMore();
  initGalleryThumbnailSelect();
  initProductCountdown();
  initAddToCartPrimaryFix();
  initStickyCta();
  initCartChangeErrorNotice();
});

/**
 * Mobile sticky Add to Cart bar (snippets/hp-sticky-cta.liquid). Its button never duplicates
 * cart/variant logic -- it just clicks the real, existing Add to Cart button, so it always
 * submits whatever variant is actually currently selected. Visible only once the real button has
 * scrolled out of view (IntersectionObserver); CSS additionally hides it entirely on desktop.
 */
function initStickyCta() {
  var bar = document.querySelector('[data-sticky-cta]');
  var realButton = document.querySelector('.ProductForm__AddToCart');
  if (!bar || !realButton) return;

  var barButton = bar.querySelector('[data-sticky-cta-button]');
  if (barButton) {
    barButton.addEventListener('click', function () {
      var current = document.querySelector('.ProductForm__AddToCart');
      if (current && !current.disabled) current.click();
    });
  }

  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.hidden = entry.isIntersecting;
        });
      },
      { threshold: 0 }
    );
    observer.observe(realButton);
  }

  document.addEventListener('variant:changed', function (event) {
    var detail = event.detail;
    if (!detail || !detail.variant) return;
    var priceEl = bar.querySelector('.StickyCta__Price');
    var buttonEl = bar.querySelector('[data-sticky-cta-button]');
    if (priceEl && detail.variant.price) {
      /* Hit Perfumes is USD-only (confirmed via Admin API) -- a hardcoded "$" is a correct,
         real value for this store, not a general-purpose assumption. */
      priceEl.textContent = formatCents(detail.variant.price);
    }
    if (buttonEl) {
      var available = detail.variant.available;
      buttonEl.disabled = !available;
      buttonEl.classList.toggle('Button--primary', available);
      buttonEl.classList.toggle('Button--secondary', !available);
    }
  });

  function formatCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

/**
 * Keeps Add to Cart black/primary after a variant change (snippets/product-form.liquid).
 *
 * ROOT CAUSE (confirmed by reading the actual bundled theme.min.js, not guessed): the theme's own
 * ProductForm class rebuilds the Add to Cart button from scratch on every variant switch
 * (`_updateAddToCartButton`) and sets its class to `Button--secondary` whenever the dynamic
 * checkout button setting (`show_payment_button`) is on -- the exact opposite of the always-black
 * primary CTA this store now requires (see the equivalent fix already made to the button's
 * INITIAL server-rendered markup in snippets/product-form.liquid; this covers the same button
 * after theme.min.js replaces it client-side). It dispatches a real `variant:changed` event right
 * after doing this, so this listens for that and corrects the freshly-replaced button in place.
 */
function initAddToCartPrimaryFix() {
  document.addEventListener('variant:changed', function (event) {
    var productSection = event.target;
    var button = productSection.querySelector ? productSection.querySelector('.ProductForm__AddToCart') : null;
    if (!button || button.disabled) return;
    button.classList.remove('Button--secondary');
    button.classList.add('Button--primary');
  });
}

/**
 * Sale countdown (snippets/hp-product-countdown.liquid). Only renders at all when a merchant has
 * turned it on AND entered a real end date/time -- see that snippet's comment. Computes remaining
 * time from the fixed target date on every tick/reload (never a page-load-relative fake timer),
 * and removes itself from the page once the target time has passed rather than freezing or
 * looping, so it can never show stale/expired "urgency".
 */
function initProductCountdown() {
  var el = document.querySelector('[data-product-countdown]');
  if (!el) return;

  var end = new Date(el.getAttribute('data-end'));
  if (isNaN(end.getTime())) {
    el.remove();
    return;
  }

  var daysEl = el.querySelector('[data-countdown-days]');
  var hoursEl = el.querySelector('[data-countdown-hours]');
  var minutesEl = el.querySelector('[data-countdown-minutes]');
  var secondsEl = el.querySelector('[data-countdown-seconds]');

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function tick() {
    var remaining = end.getTime() - Date.now();
    if (remaining <= 0) {
      el.remove();
      clearInterval(timer);
      return;
    }
    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  tick();
  var timer = setInterval(tick, 1000);
}

/**
 * Product gallery thumbnail/dot click -> swap the visible slide (sections/product-template.liquid).
 *
 * Root cause (confirmed live via Chrome/CDP): the theme's own bundled product-slideshow class
 * only ever calls `.select()`/`.selectCell()` on the Flickity instance for the MOBILE ("pocket")
 * breakpoint's variant-image-swap case. On desktop it deliberately does
 * `document.querySelector('[href="#Image' + id + '"]').click()` instead -- a plain anchor jump,
 * which was correct for the ORIGINAL desktop gallery (every image stacked vertically in normal
 * document flow, so "jump to #Image123" really did scroll the right photo into view). Once the
 * desktop gallery became a single-visible-slide Flickity carousel (matching mobile), that anchor
 * jump no longer has a meaningful, visible target to scroll to, so clicking a thumbnail neither
 * changed the visible photo nor scrolled anywhere useful.
 *
 * This intercepts the same thumbnail/dot links and calls Flickity's own real, public
 * `Flickity.data(element)` accessor to select the correct cell directly -- no reimplementation of
 * carousel logic, just correctly wiring the existing library to the existing markup.
 */
function initGalleryThumbnailSelect() {
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.Product__SlideshowNavImage, .Product__SlideshowNavDot');
    if (!link) return;

    var match = (link.getAttribute('href') || '').match(/#Image(\d+)/);
    if (!match) return;

    var gallery = link.closest('.Product__Gallery');
    var slideshowEl = gallery && gallery.querySelector('.Product__Slideshow');
    if (!slideshowEl || typeof window.Flickity === 'undefined') return;

    var flkty = window.Flickity.data(slideshowEl);
    if (!flkty) return;

    var targetImageId = match[1];
    var cellIndex = -1;
    flkty.cells.forEach(function (cell, index) {
      if (cell.element.getAttribute('data-image-id') === targetImageId) {
        cellIndex = index;
      }
    });
    if (cellIndex === -1) return;

    event.preventDefault();
    flkty.select(cellIndex);

    /* Flickity's own "select" event (wired up by the theme's bundled Slideshow class) only
       toggles mobile action-list visibility, not the thumbnail/dot highlight -- that highlight
       is normally driven by a separate scroll-spy module that never fires for a programmatic,
       scroll-less select. Keep it in sync here instead of leaving a stale selected thumbnail. */
    gallery.querySelectorAll('.Product__SlideshowNavImage, .Product__SlideshowNavDot').forEach(function (navItem) {
      var isMatch = navItem.getAttribute('href') === link.getAttribute('href');
      navItem.classList.toggle('is-selected', isMatch);
    });
  });
}

/**
 * Per-product FAQ accordion (snippets/product-faq.liquid).
 */
function initProductFaq() {
  document.querySelectorAll('[data-action="toggle-faq"]').forEach(function (button) {
    button.addEventListener('click', function () {
      var expanded = button.getAttribute('aria-expanded') === 'true';
      var answer = button.parentElement.querySelector('.ProductFaq__Answer');

      button.setAttribute('aria-expanded', String(!expanded));
      if (answer) {
        answer.hidden = expanded;
      }
    });
  });
}

/**
 * "Read full description" link in the product purchase panel (sections/product-template.liquid)
 * -- jumps to the real Description accordion item and opens it via the theme's own existing
 * .Collapsible toggle (a synthetic click on its button, so this never reimplements that logic).
 * The native anchor href already handles the scroll; this only adds the "also expand it" part.
 */
function initProductDescriptionReadMore() {
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-read-more-description]');
    if (!link) return;

    var button = document.querySelector('#ProductDescriptionAccordion .Collapsible__Button');
    if (button && button.getAttribute('aria-expanded') !== 'true') {
      button.click();
    }
  });
}

/**
 * Fragrance Finder (sections/fragrance-finder.liquid).
 *
 * A short multi-step quiz that narrows down to a real Shopify collection URL built from the
 * shop's actual gender/type/tag taxonomy. It never invents product data — each step maps to a
 * real, merchant-configured collection handle, and unanswered steps are simply skipped.
 */
function initFragranceFinder() {
  var root = document.querySelector('[data-fragrance-finder]');
  if (!root) return;

  var steps = Array.prototype.slice.call(root.querySelectorAll('[data-finder-step]'));
  var resultsEl = root.querySelector('[data-finder-results]');
  var emptyEl = root.querySelector('[data-finder-empty]');
  var answers = {};

  steps.forEach(function (step) {
    var buttons = step.querySelectorAll('[data-finder-value]');

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var group = step.getAttribute('data-finder-step');

        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        button.setAttribute('aria-pressed', 'true');

        answers[group] = button.getAttribute('data-finder-value');
        updateFinderResult();
      });
    });
  });

  function updateFinderResult() {
    var base = root.getAttribute('data-finder-base-url') || '/collections';
    var segments = [];

    Object.keys(answers).forEach(function (key) {
      if (answers[key]) segments.push(answers[key]);
    });

    if (segments.length === 0) {
      if (resultsEl) resultsEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    var url = base + '/' + segments.join('+');

    if (resultsEl) {
      resultsEl.hidden = false;
      var link = resultsEl.querySelector('[data-finder-link]');
      if (link) link.setAttribute('href', url);
    }
    if (emptyEl) emptyEl.hidden = true;
  }
}

/**
 * Subtle reveal-on-scroll for elements marked .HpReveal (category circles, editorial cards).
 * Purely decorative entrance motion -- CSS already only hides these when the .js class is
 * present, so a missing IntersectionObserver (or this script failing) never hides content
 * permanently. No animation library, native IntersectionObserver only.
 */
function initScrollReveal() {
  var items = document.querySelectorAll('.HpReveal');
  if (!items.length) return;

  if (!('IntersectionObserver' in window)) {
    items.forEach(function (item) { item.classList.add('is-visible'); });
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -20px 0px' });

  items.forEach(function (item) { observer.observe(item); });

  /* Safety net: this is decorative entrance motion, never a gate on real content, so nothing
     should ever stay hidden indefinitely if a given browser/viewport combination never fires an
     intersection (e.g. an element that's already fully visible before this script runs but
     whose initial IntersectionObserver callback got missed, or a very short/unusual viewport).
     Force everything visible after 2s regardless -- a no-op for anything already revealed. */
  window.setTimeout(function () {
    items.forEach(function (item) { item.classList.add('is-visible'); });
    observer.disconnect();
  }, 2000);
}

/**
 * Hero background slideshow (sections/hp-hero.liquid). Vanilla JS only, no carousel library:
 * toggles an .is-active class per slide/dot, CSS handles the actual opacity transition. Auto
 * -advances on a plain setInterval unless the visitor prefers reduced motion, in which case the
 * slideshow still responds to manual next/prev/dot clicks (an explicit user action, not ambient
 * motion) but never advances on its own. Falls back to nothing but a static first slide if this
 * never runs -- the first .HpHero__Slide is already marked is-active server-side in the Liquid.
 */
function initHeroSlideshow() {
  var hero = document.querySelector('[data-hero-slideshow]');
  if (!hero) return;

  var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-slide]'));
  var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-dot]'));
  var prevButton = hero.querySelector('[data-hero-prev]');
  var nextButton = hero.querySelector('[data-hero-next]');
  if (slides.length < 2) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var intervalMs = parseInt(hero.getAttribute('data-hero-interval'), 10) || 6000;
  var currentIndex = 0;
  var timer = null;

  function goTo(index) {
    currentIndex = (index + slides.length) % slides.length;

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === currentIndex);
    });
    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === currentIndex);
      dot.setAttribute('aria-selected', i === currentIndex ? 'true' : 'false');
    });
  }

  function startAutoplay() {
    if (reduceMotion) return;
    stopAutoplay();
    timer = window.setInterval(function () { goTo(currentIndex + 1); }, intervalMs);
  }

  function stopAutoplay() {
    if (timer) {
      window.clearInterval(timer);
      timer = null;
    }
  }

  if (prevButton) {
    prevButton.addEventListener('click', function () {
      goTo(currentIndex - 1);
      startAutoplay();
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', function () {
      goTo(currentIndex + 1);
      startAutoplay();
    });
  }

  dots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      goTo(i);
      startAutoplay();
    });
  });

  startAutoplay();
}

/**
 * A-Z brand index (sections/hp-brand-index.liquid). Every letter's panel already exists in the
 * real HTML (server-rendered, real vendor links, no client-side fetch) -- this only toggles
 * which one is visible, so the section is fully functional and crawlable with JS disabled too
 * (all panels simply show at once via the [hidden] attribute never being toggled off).
 */
function initBrandIndex() {
  var letters = document.querySelectorAll('[data-brand-letter]');
  if (!letters.length) return;

  letters.forEach(function (letter) {
    letter.addEventListener('click', function () {
      var targetId = letter.getAttribute('aria-controls');
      var isActive = letter.classList.contains('is-active');

      letters.forEach(function (other) {
        other.classList.remove('is-active');
        other.setAttribute('aria-expanded', 'false');
      });
      document.querySelectorAll('[data-brand-panel]').forEach(function (panel) {
        panel.hidden = true;
      });

      if (!isActive) {
        letter.classList.add('is-active');
        letter.setAttribute('aria-expanded', 'true');
        var panel = document.getElementById(targetId);
        if (panel) panel.hidden = false;
      }
    });
  });
}

/**
 * Fixes a real, confirmed bug: product images use the bundled `.Image--fadeIn` CSS pattern,
 * which only reaches opacity: 1 once the theme's own lazy-load library adds an
 * `Image--lazyLoaded` class on that specific <img> (see theme.scss.liquid's
 * `.Image--lazyLoaded.Image--fadeIn` rule). Confirmed live: the same real product image,
 * repeated in more than one homepage section (e.g. once in Best Sellers, again in a gender
 * showcase's product rail), loads correctly every time (naturalWidth > 0, complete: true) but
 * only the FIRST occurrence ever receives `Image--lazyLoaded` -- later occurrences of an
 * already-cached image apparently resolve before the lazy-load library's own load listener
 * attaches to them, so they stay invisible at opacity: 0 forever despite being fully loaded.
 * This adds the missing class directly whenever an image is actually loaded but never got it,
 * checked a few times after load to catch any timing race, without touching the vendored
 * theme.min.js.
 */
function initLazyImageFadeInFix() {
  function reveal() {
    document.querySelectorAll('.Image--fadeIn:not(.Image--lazyLoaded)').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('Image--lazyLoaded');
      }
    });
  }

  reveal();
  window.addEventListener('load', reveal);
  window.setTimeout(reveal, 500);
  window.setTimeout(reveal, 1500);
  window.setTimeout(reveal, 3000);
}

/**
 * Fixes a real, confirmed bug: the vendored theme.min.js quantity-stepper handler in the cart
 * drawer/page (bound to the -/+ links, POSTs to /cart/change.js) always calls response.json()
 * and re-renders the cart from it, without ever checking response.ok or the JSON body's own
 * `status`/`message` fields first. When Shopify rejects a quantity change (its real, valid
 * failure mode for exceeding available inventory -- confirmed live: requesting quantity 2 on a
 * real product with exactly 1 unit in stock returns HTTP 422 with
 * {"status":422,"message":"Only 1 item was added to your cart due to availability."}), that
 * message is silently discarded: the cart drawer just re-renders the unchanged cart with no
 * explanation, so a shopper sees the quantity snap back with zero feedback. This wraps fetch to
 * surface that real, pre-existing message in a small toast, without touching the vendored
 * quantity-update logic itself (which already works correctly otherwise).
 */
function initCartChangeErrorNotice() {
  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartChange = url.indexOf('/cart/change.js') !== -1 || url.indexOf('/cart/add.js') !== -1;

    return originalFetch.apply(window, arguments).then(function (response) {
      if (isCartChange) {
        response
          .clone()
          .json()
          .then(function (body) {
            if (body && body.status && body.status >= 400 && body.message) {
              showCartNotice(body.message);
            }
          })
          .catch(function () {});
      }
      return response;
    });
  };

  function showCartNotice(message) {
    var existing = document.getElementById('HpCartNotice');
    if (existing) existing.remove();

    var notice = document.createElement('div');
    notice.id = 'HpCartNotice';
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    notice.style.cssText =
      'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:10000;' +
      'background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:4px;font-size:14px;' +
      'max-width:90vw;text-align:center;box-shadow:0 4px 12px rgba(0,0,0,0.25);';
    document.body.appendChild(notice);

    window.setTimeout(function () {
      notice.remove();
    }, 4000);
  }
}

/**
 * Collection filter sidebar/drawer (sections/collection-template.liquid). Progressively
 * enhances real, already-functional Shopify filtering:
 *  - the dual-handle price range slider (two overlapping <input type="range">, a well-known
 *    accessible/lightweight pattern -- no external slider library);
 *  - "Show more" / "Show less" for any facet list longer than 9 real values;
 *  - auto-submitting the real <form> the moment a checkbox changes, so filtering feels instant
 *    instead of requiring an explicit Apply click on desktop (the real "Apply" submit button
 *    stays as the genuine no-JS fallback, and is the primary action in the mobile drawer).
 * Every filter this touches is still a real Shopify collection.filters value -- this file never
 * invents a filter, a count, or a URL.
 */
function initCollectionFacets() {
  document.querySelectorAll('.HpPriceSlider').forEach(initPriceSlider);

  document.querySelectorAll('[data-action="toggle-show-more"]').forEach(function (button) {
    button.addEventListener('click', function () {
      var list = button.previousElementSibling;
      var expanded = button.getAttribute('aria-expanded') === 'true';

      list.querySelectorAll('[data-facet-extra]').forEach(function (item) {
        item.hidden = expanded;
      });

      button.setAttribute('aria-expanded', String(!expanded));
      button.textContent = expanded
        ? button.getAttribute('data-show-more-text')
        : button.getAttribute('data-show-less-text');
    });
  });

  document.querySelectorAll('.HpFacetsForm').forEach(function (form) {
    form.querySelectorAll('.HpFacet__Checkbox').forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        form.submit();
      });
    });
  });
}

function initPriceSlider(slider) {
  var minInput = slider.querySelector('[data-price-input="min"]');
  var maxInput = slider.querySelector('[data-price-input="max"]');
  var rangeEl = slider.querySelector('[data-price-range]');
  var minDisplay = slider.parentElement.querySelector('[data-price-display="min"]');
  var maxDisplay = slider.parentElement.querySelector('[data-price-display="max"]');
  if (!minInput || !maxInput) return;

  var bounds = { min: parseFloat(slider.getAttribute('data-min')) || 0, max: parseFloat(slider.getAttribute('data-max')) || 0 };
  if (bounds.max <= bounds.min) return;

  function formatMoney(value) {
    // Matches the theme's own money format closely enough for a live preview label; the real,
    // shop-formatted amount is whatever collection.filters renders server-side after the form
    // actually submits -- this is just the interactive slider's own live readout.
    return '$' + Number(value).toFixed(2);
  }

  function update() {
    var minVal = parseFloat(minInput.value);
    var maxVal = parseFloat(maxInput.value);

    if (minVal > maxVal) {
      // Keep the handle that's being dragged from crossing the other one.
      if (document.activeElement === minInput) {
        minVal = maxVal;
        minInput.value = String(minVal);
      } else {
        maxVal = minVal;
        maxInput.value = String(maxVal);
      }
    }

    var range = bounds.max - bounds.min;
    var leftPercent = range > 0 ? ((minVal - bounds.min) / range) * 100 : 0;
    var rightPercent = range > 0 ? ((maxVal - bounds.min) / range) * 100 : 100;

    if (rangeEl) {
      rangeEl.style.left = leftPercent + '%';
      rangeEl.style.right = (100 - rightPercent) + '%';
    }
    if (minDisplay) minDisplay.textContent = formatMoney(minVal);
    if (maxDisplay) maxDisplay.textContent = formatMoney(maxVal);
  }

  minInput.addEventListener('input', update);
  maxInput.addEventListener('input', update);

  var form = slider.closest('form');
  if (form) {
    [minInput, maxInput].forEach(function (input) {
      input.addEventListener('change', function () {
        form.submit();
      });
    });
  }

  update();
}
